import { useMemo, useState } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { buildBetaFeedbackMailto } from "@/lib/betaFeedback";

type BetaFeedbackDialogProps = {
  workflow?: string;
  registrars?: string[];
  variant?: "default" | "outline" | "ghost";
  compact?: boolean;
  buttonLabel?: string;
};

export function BetaFeedbackDialog({
  workflow = "",
  registrars = [],
  variant = "outline",
  compact = false,
  buttonLabel = "Beta Feedback",
}: BetaFeedbackDialogProps) {
  const [open, setOpen] = useState(false);
  const [workflowInput, setWorkflowInput] = useState(workflow);
  const [portfolioSize, setPortfolioSize] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [problem, setProblem] = useState("");
  const [expected, setExpected] = useState("");

  const mailtoHref = useMemo(
    () =>
      buildBetaFeedbackMailto({
        workflow: workflowInput,
        registrars,
        portfolioSize,
        teamSize,
        problem,
        expected,
      }),
    [expected, portfolioSize, problem, registrars, teamSize, workflowInput]
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={compact ? "sm" : "default"} className={compact ? "" : "gap-2"}>
          <MessageSquare className="h-4 w-4" />
          {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share beta feedback</DialogTitle>
          <DialogDescription>
            Keep this tied to one workflow so the beta feedback stays actionable.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="beta-workflow">Workflow</Label>
            <Input
              id="beta-workflow"
              value={workflowInput}
              onChange={(e) => setWorkflowInput(e.target.value)}
              placeholder="providers, domains, api keys..."
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="beta-portfolio-size">Portfolio size</Label>
              <Input
                id="beta-portfolio-size"
                value={portfolioSize}
                onChange={(e) => setPortfolioSize(e.target.value)}
                placeholder="100 domains, 750 domains, etc."
              />
            </div>
            <div>
              <Label htmlFor="beta-team-size">Team size</Label>
              <Input
                id="beta-team-size"
                value={teamSize}
                onChange={(e) => setTeamSize(e.target.value)}
                placeholder="solo, 2 people, 5 people..."
              />
            </div>
          </div>
          <div>
            <Label htmlFor="beta-problem">What blocked you?</Label>
            <Textarea
              id="beta-problem"
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              placeholder="Describe the step that failed or felt unclear."
              rows={4}
            />
          </div>
          <div>
            <Label htmlFor="beta-expected">What did you expect instead?</Label>
            <Textarea
              id="beta-expected"
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              placeholder="Describe the expected outcome or workflow."
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button asChild>
            <a href={mailtoHref} onClick={() => setOpen(false)}>
              Email Feedback
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
